import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { currentMonth, currentYear, getMonthOptions, YEARS } from "../../variables/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import "./styles.scss";
import { apiEndPoints } from "../../variables/apiEndpoints";
import useFileDownloader from "../../hooks/useFileDownloader";
import CustomSnackbar from "../../components/common/Snackbar/CustomSnackbar";
import { getAccounts } from "../../services/accountService";
import AutoCompleteSelect from "../../components/common/autocompleteSelect/AutoCompleteSelect";

const ClientStatements = () => {
  const { id } = useSelector((state: RootState) => state.userDetails);
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [account, setAccount] = useState<any>("0");
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [isLoading, setIsLoading] = useState(false);
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const { downloadFile } = useFileDownloader();

  useEffect(() => {
    getAccounts()
      ?.then((res: any) => {
        const accounts: any[] = [];
        res?.forEach((acc: any) => {
          if (acc.isActive) {
            accounts.push({ value: acc.id, label:  `${acc.name} - ${acc.accountNumber}`, });
          }
        });
        setAccountOptions(accounts);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, [id]);

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    const body = {
      client: {
        filter: [],
        isActive: true,
      },
      account: {
        filter: account === "0" ? [] : [account],
        isActive: true,
      },
      month: selectedMonth,
      year: selectedYear,
    };
    const result = await downloadFile({
      url: `/reports${apiEndPoints.accounts}`,
      method: "post",
      fileName: "ClientAccountStatements.pdf",
      data: body,
    });
    setIsLoading(false);
    if (!result.success) {
      setSnackbarMsg(result.errorMessage || "Error downloading file");
      setOnSuccess(true);
    }
  }, [downloadFile]);

  return (
    <>
      <Toolbar className="toolbar">
        <Typography className="title">Statements</Typography>
      </Toolbar>
      <Paper className="container pd-20">
        <Stack spacing={3}>
          <Typography variant="h5" component="h3">
            Create Account Statement
          </Typography>
          <Box className="form-group">
            <AutoCompleteSelect
              items={[{ value: "0", label: "All Accounts" }, ...accountOptions]}
              onSelectItem={setAccount}
              selected={account?.toString() ?? ""}
              placeholder="Select an account"
              label="Account"
              width="212px"
              maxWidthOption="212px"
              defaultValue={{ label: "All Accounts", value: "0" }}
            />
            <FormGroup className="form-group">
              <FormControl className="dropdown-month" size="small">
                <InputLabel id="demo-simple-select-label">Month</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(e.target.value as number)}
                >
                  {getMonthOptions().map((month) => (
                    <MenuItem
                      key={month.value}
                      value={month.value}
                      selected={month.default}
                    >
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="dropdown-year" size="small">
                <InputLabel id="demo-simple-select-label">Year</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value as number)}
                >
                  {YEARS.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormGroup>
          </Box>
          <Divider className="divider" />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", height: "36px" }}
          >
            <LoadingButton
              variant="contained"
              data-testid="createTxnBtn"
              size="small"
              onClick={handleDownload}
              loading={isLoading}
            >
              Create STATEMENT
            </LoadingButton>
          </Box>
        </Stack>
        <CustomSnackbar
          open={onSuccess}
          message={snackbarMsg}
          setOpen={setOnSuccess}
        />
      </Paper>
    </>
  );
};

export default ClientStatements;
